import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Mail, Copy, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/waitlist")({
  component: AdminWaitlistPage,
});

type WaitlistEntry = {
  email: string;
  tier: string;
  created_at: string;
};

function AdminWaitlistPage() {
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "paid-waitlist"],
    queryFn: async (): Promise<WaitlistEntry[]> => {
      const { data: rows, error } = await supabase
        .from("paid_waitlist" as never)
        .select("email, tier, created_at")
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message);
      return (rows ?? []) as WaitlistEntry[];
    },
  });

  const entries = data ?? [];

  const copyAll = async () => {
    if (entries.length === 0) return;
    const list = entries.map((e) => e.email).join(", ");
    try {
      await navigator.clipboard.writeText(list);
      setCopied(true);
      toast.success(`Copied ${entries.length} email${entries.length === 1 ? "" : "s"} to clipboard.`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not access the clipboard in this browser.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-gold" />
            <h1 className="font-display text-3xl font-bold">Waitlist</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Emails registered for notification when paid enrolment opens. One email per entry, no marketing sequence.
          </p>
        </div>
        <Button variant="outline" onClick={copyAll} disabled={entries.length === 0 || copied}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy all"}
        </Button>
      </div>

      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Total entries</span>
        <span className="font-display text-lg font-bold text-foreground">{entries.length}</span>
      </div>

      <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading.</div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No entries match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 text-left font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Tier</th>
                  <th className="px-4 py-3 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {entries.map((e) => (
                  <tr key={`${e.email}-${e.tier}`} className="hover:bg-muted/40">
                    <td className="px-4 py-3">{e.email}</td>
                    <td className="px-4 py-3">
                      <span className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        {e.tier}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {new Date(e.created_at).toISOString().slice(0, 16).replace("T", " ")} UTC
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
